import { inject } from 'vue'
import { RequestService } from './RequestService'
import { get } from 'lodash'

/**
 * ComplianceAuditLogService — frontend service to interact with the Compliance Audit Log API.
 * Tracks changes to assessment questions that generate or resolve action items.
 */
export const ComplianceAuditLogService = () => {
  const { post, simpleGet, handleErrors } = RequestService()
  const functionsBaseUrl = inject('functionsBaseUrl')

  /**
   * Check whether an initial snapshot already exists for the given plan year.
   * @param {string} planYearId
   * @returns {Promise<boolean>}
   */
  async function checkHasInitialSnapshot(planYearId) {
    try {
      const response = await simpleGet(
        'hasInitialSnapshot',
        `${functionsBaseUrl}/questionsGateway/api/v1/plan-years/${planYearId}/audit-trail/has-snapshot`
      )
      return get(response, 'data.data.hasSnapshot', false)
    } catch (error) {
      handleErrors('checkHasInitialSnapshot', error)
      return false
    }
  }

  /**
   * Record the initial snapshot when the assessment is completed for the first time.
   * Creates one record per question that currently has an active action item.
   * @param {Object} params
   * @param {string} params.employerId
   * @param {string} params.planYearId
   * @param {string} params.agencyId
   * @param {Array<string>} params.actionItemQuestionIds - IDs of questions with active action items
   * @param {Object} params.complianceSnapshotAfter - { total_compliance_risk, percentage_compliance_risk }
   * @param {Object} params.questionsMap - Map of questionId -> { answer } for current answers
   */
  async function recordInitialSnapshot({
    employerId,
    planYearId,
    agencyId,
    actionItemQuestionIds,
    complianceSnapshotAfter,
    questionsMap
  }) {
    try {
      if (!actionItemQuestionIds || actionItemQuestionIds.length === 0) return

      const records = actionItemQuestionIds.map(questionId => ({
        employer_id: String(employerId),
        plan_year_id: String(planYearId),
        agency_id: agencyId ? String(agencyId) : null,
        question_id: String(questionId),
        source: 'assessment_tool',
        change_type: 'initial_snapshot',
        previous_answer: null,
        new_answer: questionsMap[questionId] ?? null,
        previous_action_item_ids: null,
        new_action_item_ids: actionItemQuestionIds.map(String),
        compliance_snapshot_before: null,
        compliance_snapshot_after: complianceSnapshotAfter
      }))

      await post(
        'saveComplianceAuditLog',
        `${functionsBaseUrl}/questionsGateway/api/v1/audit-trail`,
        records
      )
    } catch (error) {
      handleErrors('recordInitialSnapshot', error)
    }
  }

  /**
   * Record a single answer change that affected action item status.
   * @param {Object} params
   * @param {string} params.employerId
   * @param {string} params.planYearId
   * @param {string} params.agencyId
   * @param {string} params.questionId
   * @param {string} params.source - 'assessment_tool' | 'action_center'
   * @param {string} params.changeType - 'action_item_created' | 'action_item_resolved'
   * @param {*} params.previousAnswer
   * @param {*} params.newAnswer
   * @param {Array<string>} params.previousActionItemIds
   * @param {Array<string>} params.newActionItemIds
   * @param {Object} params.complianceSnapshotBefore - { total_compliance_risk, percentage_compliance_risk }
   * @param {Object} params.complianceSnapshotAfter - { total_compliance_risk, percentage_compliance_risk }
   */
  async function recordAnswerChange({
    employerId,
    planYearId,
    agencyId,
    questionId,
    source,
    changeType,
    previousAnswer,
    newAnswer,
    previousActionItemIds,
    newActionItemIds,
    complianceSnapshotBefore,
    complianceSnapshotAfter
  }) {
    try {
      const record = {
        employer_id: String(employerId),
        plan_year_id: String(planYearId),
        agency_id: agencyId ? String(agencyId) : null,
        question_id: String(questionId),
        source,
        change_type: changeType,
        previous_answer: previousAnswer ?? null,
        new_answer: newAnswer ?? null,
        previous_action_item_ids: previousActionItemIds ? previousActionItemIds.map(String) : null,
        new_action_item_ids: newActionItemIds ? newActionItemIds.map(String) : null,
        compliance_snapshot_before: complianceSnapshotBefore,
        compliance_snapshot_after: complianceSnapshotAfter
      }

      await post(
        'saveComplianceAuditLog',
        `${functionsBaseUrl}/questionsGateway/api/v1/audit-trail`,
        [record]
      )
    } catch (error) {
      handleErrors('recordAnswerChange', error)
    }
  }

  /**
   * Retrieve the full audit trail for a plan year (for future UI usage).
   * @param {string} planYearId
   * @returns {Promise<Array>}
   */
  async function getAuditTrail(planYearId) {
    try {
      const response = await simpleGet(
        'getComplianceAuditLog',
        `${functionsBaseUrl}/questionsGateway/api/v1/plan-years/${planYearId}/audit-trail`
      )
      return get(response, 'data.data', [])
    } catch (error) {
      handleErrors('getAuditTrail', error)
      return []
    }
  }

  return {
    checkHasInitialSnapshot,
    recordInitialSnapshot,
    recordAnswerChange,
    getAuditTrail
  }
}
